<template>
  <v-container class="fill-height">
    <v-row>
      <v-col cols="12" sm="6">
        <v-container class="pa-12">
          <div class="text-center px-4">
            <img
              class="center"
              :srcset="`${vendorproofLogo1x} 1x, ${vendorproofLogo2x} 2x`"
              alt="VendorProof Logo"
              width="344"
              height="50"
            />
          </div>
          <div class="mb-3 mt-8 px-4">
            <h2 class="h1-desktop-36">
              Become one of healthcare's most trusted vendors with VendorProof.
            </h2>
          </div>
          <div class="px-4">
            <v-list color="transparent">
              <v-list-item v-for="(item, index) in listItems" :key="index">
                <v-list-item-icon>
                  <div class="circle-icon mt-1 mr-n2">
                    <div class="checkmark">
                      <div class="checkmark-stem"></div>
                      <div class="checkmark-kick"></div>
                    </div>
                  </div>
                </v-list-item-icon>
                <v-list-item-content class="h3-desktop-regular-20">
                  {{ item }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-container>
      </v-col>
      <v-col cols="12" sm="6">
        <pt-sheet v-if="!passwordExpired" no-highlight>
          <v-card-title class="text-center px-8">
            <img
              class="center"
              src="@/assets/vendorproof-shadowmark@2x.png"
              alt="VendorProof Logo"
              width="82px"
              height="82px"
            />
          </v-card-title>
          <v-card-subtitle class="my-4 px-8">
            <h2 class="title text-center">
              Login to VendorProof to access your dashboard.
            </h2>
          </v-card-subtitle>
          <v-card-text class="px-8">
            <v-form @submit.prevent="login">
              <v-text-field outlined label="Username" v-model="username" />
              <v-text-field
                outlined
                label="Password"
                v-model="password"
                :type="show ? 'text' : 'password'"
                :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show = !show"
              />
              <p>
                <a class="link" href="/reset-password">Forgot Password?</a>
              </p>
              <div class="my-4">
                <large-blue-call-to-action
                  type="submit"
                  block
                  @click.prevent="login"
                  :loading="waitingForResponse"
                  :disabled="waitingForResponse"
                >
                  Log In
                </large-blue-call-to-action>
              </div>
              <p class="body-desktop-medium-regular-16">
                <span class="mr-1">Don't have an account?</span>
                <router-link class="link" :to="{ name: 'CreateAccount' }"
                  >Create Account</router-link
                >
              </p>
            </v-form>
          </v-card-text>
        </pt-sheet>
        <pt-sheet v-else no-highlight>
          <v-card-title class="text-center px-8">
            <img
              class="center"
              src="@/assets/vendorproof-shadowmark@2x.png"
              alt="VendorProof Logo"
              width="82px"
              height="82px"
            />
          </v-card-title>
          <v-card-subtitle class="my-4 px-8">
            <h2 class="title text-center">
              Set a new password
            </h2>
          </v-card-subtitle>
          <v-card-text class="px-8">
            <p>{{ newPasswordRules }}</p>
            <v-form v-model="formValid" @submit.prevent="resetPassword">
              <v-text-field
                outlined
                label="Username"
                v-model="username"
                disabled
              />
              <v-text-field
                outlined
                label="New Password"
                v-model="newPassword"
                :type="show ? 'text' : 'password'"
                :rules="passwordRules"
                :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show = !show"
              />
              <v-text-field
                outlined
                label="Verify New Password"
                v-model="verifyNewPassword"
                :type="show ? 'text' : 'password'"
                :rules="passwordRules"
                :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show = !show"
              />
              <div class="my-4">
                <large-blue-call-to-action
                  block
                  type="submit"
                  :disabled="btnDisabled"
                  disabled-tooltip-message="Please fill out all required fields"
                  :loading="waitingForResponse"
                >
                  Set New Password
                </large-blue-call-to-action>
              </div>
            </v-form>
          </v-card-text>
        </pt-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PtSheet from "@/components/PtSheet.vue";
import vendorproofLogo1x from "@/assets/vendorproof-logo@1x.png";
import vendorproofLogo2x from "@/assets/vendorproof-logo@2x.png";
import { post } from "@/services/request";
import { loginAPI, passwordResetAPI } from "@/services/api";
import LargeBlueCallToAction from "@/components/providertrust/buttons/LargeBlueCallToAction.vue";
import hasNotifications from "@/mixins/has-notifications";
import { passwordRegexRules, valuesMatch } from "@/services/rules";
import * as rules from "@/services/rules";
import store from "@/store";
import { getCurrentUser } from "@/services/shared";
import { initializePendo } from "@/services/pendoInit";

export default {
  name: "VendorLogin",
  components: { LargeBlueCallToAction, PtSheet },
  mixins: [hasNotifications],
  data: () => ({
    username: "",
    password: "",
    newPassword: "",
    verifyNewPassword: "",
    newPasswordRules: "",
    show: false,
    passwordExpired: false,
    listItems: [
      "Centralize critical information your healthcare partners need.",
      "Straightforward onboarding helps your business move faster.",
      "Ensure contract compliance with all healthcare-specific requirements."
    ],
    vendorproofLogo1x,
    vendorproofLogo2x,
    waitingForResponse: false,
    formValid: false
  }),
  computed: {
    passwordRules() {
      return passwordRegexRules.concat([
        rules.required(),
        valuesMatch(
          this.newPassword,
          this.verifyNewPassword,
          "Passwords must match"
        )
      ]);
    },
    btnDisabled() {
      return !this.formValid || this.waitingForResponse;
    }
  },
  methods: {
    async login() {
      this.waitingForResponse = true;
      try {
        const response = await post(loginAPI, {
          username: this.username,
          password: this.password
        });

        if (response.data.status === "authenticated") {
          this.clearMessage();

          await this.postAuthenticatedReloadsAndRedirect();
        } else if (response.data.status === "expired") {
          // handle password expiration
          console.log(response.data.messages[0]);
          this.passwordExpired = true;
          this.newPasswordRules = response.data.messages[0];

          this.showMessage({
            text: "Your password has expired. Please set a new password.",
            mode: "warning"
          });
        } else if (response.data.status === "locked") {
          // handle locked account
          console.log(response.data.messages[0]);
          this.showMessage({
            text: response.data.messages[0],
            mode: "error"
          });
        } else {
          // handle failed login
          // covers "failed" and "invalid" status plus any unknown statuses
          console.log(response.data.messages[0]);
          this.showMessage({
            text: response.data.messages[0],
            mode: "error"
          });
        }
      } catch (error) {
        this.showMessage({
          text: "Encountered error logging in. Please try again.",
          mode: "error"
        });
      } finally {
        this.waitingForResponse = false;
      }
    },
    postAuthenticatedReloadsAndRedirect: async function() {
      await store.dispatch("user/loadCurrentUser");
      await store.dispatch("featureFlags/fetchFlags");

      const currentUser = getCurrentUser();
      //init pendo tracking now that user info exists
      initializePendo(currentUser);

      if (currentUser.role === "Client User") {
        window.location.href = "/client/dashboard";
      } else {
        await this.$router.push({ name: "dashboard" });
      }
    },
    resetPassword() {
      this.waitingForResponse = true;
      post(passwordResetAPI, {
        username: this.username,
        oldPassword: this.password,
        newPassword: this.newPassword
      })
        .then(async response => {
          if (response.data.status === "authenticated") {
            // Password was successfully changed and user is authenticated
            this.passwordExpired = false;
            this.clearMessage();
            await this.postAuthenticatedReloadsAndRedirect();
          } else if (response.data.status === "failed") {
            // The password change failed
            console.log(response.data.messages.join(", "));
            this.showMessage({
              text: response.data.messages.join(", "),
              mode: "error"
            });
          }
        })
        .catch(() => {
          this.showMessage({
            text: "Encountered error logging in. Please try again.",
            mode: "error"
          });
          this.passwordExpired = false;
        })
        .finally(() => {
          this.waitingForResponse = false;
        });
    }
  }
};
</script>

<style scoped lang="scss">
.link {
  text-decoration: underline;
}

.circle-icon {
  background: $primary;
  border-radius: 50%;
  height: 37px;
  width: 37px;
  position: relative;
}

.checkmark {
  width: 24px;
  height: 24px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
}

.checkmark-stem {
  position: absolute;
  width: 2px;
  height: 18px;
  background-color: #ffffff;
  left: 67%;
  top: 17%;
  margin-left: -2px;
  transform: rotate(45deg);
  border-radius: 1px;
}

.checkmark-kick {
  position: absolute;
  width: 9px;
  height: 2px;
  background-color: #ffffff;
  left: 30%;
  top: 64%;
  margin-left: -5px;
  transform: rotate(45deg);
  border-radius: 1px;
}
</style>
